import React, { useEffect, memo, useState, useCallback, useMemo } from "react";
import { useT, useCtx, withRedux, WithReduxHocPropsType } from "@al/next/al-ex";
import { FlatList } from "@al/akui-mobile";
import { PriceTools, CountryCode } from "@al/base-lib";

import linkTo from "~/common/utils/linkTo";
import { CouponItemModel, CouponStatus, DiscountType, CouponType } from "~/common/model/coupon";
import { CouponItem, NoCoupon } from "~/components/ui/coupon";
import HalfPopup from "~/components/ui/HalfPopup";
import { tracker } from "~/common/tracker";

import { getTrackerSaReportInfo } from "../../../../sensor-config";
import FunctionItem from "../item";
import redux from "../../../redux";
import { ProductDetailType, SkuInfoType } from "../../../types";

import styles from "./styles.scss";

interface Props extends WithReduxHocPropsType {
  detail: ProductDetailType;
  currentSku: SkuInfoType;
  isSign: boolean;
  openAppLink: string;
}

const Coupon: React.FC<Props> = (props) => {
  const [coupons, setCoupons] = useState<CouponItemModel[] | null>([]);
  const [visible, setVisible] = useState(false);
  const t = useT();
  const ctx = useCtx();
  const detail = props.detail;
  const currentSku = props.currentSku || {};

  async function getCoupons(): Promise<void> {
    const { success, data } = await props.dispatch(
      redux.actions.asyncGetProductCoupons({
        skuId: currentSku.skuId,
        spuId: detail.spuId,
        vendorId: detail.vendorId,
        shopId: detail.shopId,
        categoryIds: detail.categoryInfos?.map((item) => item.id).join(":") || [],
      })
    );
    if (success) {
      setCoupons(
        data?.coupons?.filter(
          (item: CouponItemModel) =>
            item.couponType === CouponType.ECommerce ||
            item.couponType === CouponType.Shop ||
            item.couponType === CouponType.Product
        ) || []
      );
    }
  }

  useEffect(() => {
    detail && currentSku.skuId && getCoupons();
  }, [detail, currentSku]);

  const showPopup = useCallback((): void => {
    setVisible(true);
    tracker.click({
      sa: getTrackerSaReportInfo({ page: "homePage", eventName: "clickCouponPop" }),
    });
  }, []);

  const closePopup = useCallback((): void => {
    setVisible(false);
    tracker.popClick({
      sa: {
        pop_id: `pop30274`,
        pop_name: "coupon",
        element_id: `pop3027401`,
        element_name: "close",
      },
    });
  }, []);

  const keyExtractor = useCallback((item: CouponItemModel, index: number): string => `${item.couponId}_${index}`, []);

  const onGetCoupon = useCallback((): void => {
    tracker.popClick({
      sa: getTrackerSaReportInfo({ page: "homePage", popName: "couponPop", eventName: "clickCoupon" }),
    });
    props?.openAppLink && linkTo(props.openAppLink);
  }, [props?.openAppLink]);

  const renderItem = useCallback(
    (data: { item: CouponItemModel; index: number }) => {
      const isReceivable = !!data.item.couponId;
      return (
        <CouponItem
          operateType="get"
          hasReceived={isReceivable}
          onSelect={(): void => {
            !isReceivable && onGetCoupon();
          }}
          data={data.item}
          couponStatus={CouponStatus.UnUsed}
          noDescription
          key={`${data.item.couponId}_${data.index}`}
        />
      );
    },
    [props.isSign, currentSku, onGetCoupon]
  );

  // 排序，按金额排序, 并且过滤非电商券的优惠券
  const sortAble = useMemo(() => {
    if (coupons?.length) {
      return coupons
        .slice()
        .sort((a, b) => Number(b.faceAmount) - Number(a.faceAmount))
        .slice(0, 2);
    }
    return [];
  }, [coupons]);

  if (coupons?.length === 0) {
    return null;
  }

  return (
    <>
      <FunctionItem
        title={t("优惠券")}
        subTitleClassName={styles.coupon_list}
        subTitle={sortAble.map((item, i) => {
          const isPercentType = item.discountType === DiscountType.FullDiscount;
          return (
            (
              <div key={`${item.couponId}_${i}`} className={styles.coupon}>
                <span className={styles.coupon_amount}>
                  {isPercentType
                    ? item.faceAmount
                    : PriceTools.formatPrice(ctx.locale.countryCode as CountryCode, item.faceAmount, "price")}
                  {isPercentType ? `${item.faceAmount}%` : ""}
                </span>
              </div>
            ) ?? null
          );
        })}
        onClick={showPopup}
      />
      <HalfPopup
        visible={visible}
        title={t("优惠券")}
        onMaskClick={closePopup}
        onClose={closePopup}
        headerTextAlign="left"
        destroyOnClose
        className={styles.coupon_pop}
        bodyClassName={styles.coupon_pop_body}
        contentClassName={styles.coupon_pop_content}
      >
        <div className={styles.tip}>{t("可领取的优惠券")}</div>
        <div className={styles.scroll_container}>
          <FlatList
            data={coupons}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            ListEmptyComponent={<NoCoupon />}
            ItemSeparatorComponent={<div className={styles.separator}></div>}
          />
        </div>
      </HalfPopup>
    </>
  );
};

export default withRedux((state) => ({
  currentSku: state.static.currentSku,
  isSign: state.sign.isSign,
  openAppLink: state.static.openAppLink,
}))(memo(Coupon));
